import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { 
  ArrowLeft, 
  RefreshCw, 
  Save, 
  RotateCcw, 
  ChevronLeft, 
  ChevronRight, 
  Download,
  Upload,
  Search,
  AlertCircle
} from 'lucide-react';
import Select from 'react-select';
// import * as XLSX from 'xlsx';
import * as XLSX from "xlsx-js-style";
import useFetch from '../constants/useFetch';
import { API_BASE_URL } from '../constants';

const EnterClassResult = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { classId } = useParams();
  const searchParams = new URLSearchParams(location.search);

  const classParam = searchParams.get('classOption');
  const subjectParam = searchParams.get('subjectOption');
  const termParam = searchParams.get('termOption');
  const sessionParam = searchParams.get('sessionOption');
  
  const { user } = useSelector((state) => state.auth);
  const { teacherData } = useSelector((state) => state.teacherData || {});

//   console.log({currentSession: user.currentSession}); //2024/2025

  // Get class info from Redux or URL params
  const currentClass = teacherData?.classrooms?.find(cls => cls.uuid === classId);
  const currentSubject = teacherData?.subjects?.[0] || user?.subject || null;

  const dataUrl = `${API_BASE_URL}/api/web/staff/dashboard-data`;
  const { data: dashboardData, loading: dashboardLoading, error: dashboardError, refetch: refetchDashboard } = useFetch(dataUrl, 'dashboardData');
  // console.log({dashboardData});
  // State for saving bulk & single result
  const [isLoadingResult, setIsLoadingResult] = useState(false);
  const [loading, setLoading] = useState(false);
  const [savingStudentId, setSavingStudentId] = useState(null);

  // State for form selections
  const [classValue, setClassValue] = useState(null);
  const [subjectValue, setSubjectValue] = useState(null);
  const [termValue, setTermValue] = useState({ value: 'first', label: 'First Term' });
  const [sessionValue, setSessionValue] = useState({ value: '2024-2025', label: '2024/2025' });
  const [examDate, setExamDate] = useState('');
  
  // State for students and table
  const [showStudentsSection, setShowStudentsSection] = useState(false);
  const [studentsData, setStudentsData] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const fileInputRef = useRef(null);
  
  // Refs to track if values have been manually changed
  const classManuallyChanged = useRef(false);
  const subjectManuallyChanged = useRef(false);
  const initializedFromParams = useRef(false);

  // Options for select dropdowns
  const classAllOptions = [
    { value: 'jss1a', label: 'JSS1A' },
    { value: 'jss1b', label: 'JSS1B' },
    { value: 'jss2a', label: 'JSS2A' },
    { value: 'jss2b', label: 'JSS2B' },
    { value: 'jss3a', label: 'JSS3A' },
    { value: 'jss3b', label: 'JSS3B' },
    { value: 'sss1a', label: 'SSS1A' },
    { value: 'sss1b', label: 'SSS1B' },
    { value: 'sss2a', label: 'SSS2A' },
    { value: 'sss2b', label: 'SSS2B' },
    { value: 'sss3a', label: 'SSS3A' },
    { value: 'sss3b', label: 'SSS3B' }
  ];

  // Options for select dropdowns
  const classOptions = useMemo(() => {
    return dashboardData?.classrooms?.map(cls => ({ 
        value: cls.classroom_slug, 
        label: cls.class_name 
      })) || [];
  }, [dashboardData]);

  const subjectOptions = useMemo(() => {
    return dashboardData?.subjects?.map(subj => ({ 
      value: subj.slug, 
      label: subj.subject_name 
    })) || [];
  }, [dashboardData]);
  // console.log({subjectOptions});
  
  const termOptions = [
    { value: 'first', label: 'First Term' },
    // { value: 'second', label: 'Second Term' },
    // { value: 'third', label: 'Third Term' }
  ];

  const sessionOptions = [
    { value: '2025-2026', label: '2025/2026' },
    // { value: '2024-2025', label: '2024/2025' },
    // { value: '2023-2024', label: '2023/2024' },
  ];

  // Initialize form values from current context (only on mount or when query params change)
  useEffect(() => {
    // Only initialize from query params if not already manually changed and options are available
    if (classOptions.length > 0 && subjectOptions.length > 0) {
      // Initialize class from query param if available and not manually changed
      if (classParam && !classManuallyChanged.current && Array.isArray(classOptions)) {
        const match = classOptions?.find(opt => opt.value === classParam.toLowerCase());
        if (match && (!classValue || classValue.value !== match.value)) {
          setClassValue(match);
        }
      }
      
      // Initialize subject from query param if available and not manually changed
      if ((subjectParam || currentSubject) && !subjectManuallyChanged.current) {
        const match = subjectOptions?.find(opt => opt.value === subjectParam?.toLowerCase() || opt.value === currentSubject?.subject_name?.toLowerCase());
        if (match && (!subjectValue || subjectValue.value !== match.value)) {
          setSubjectValue(match);
        }
      }
      
      // Initialize term and session from user data (only once)
      if (!initializedFromParams.current) {
        if (user?.currentTerm) {
          const termMatch = termOptions.find(opt => opt.value === user.currentTerm.toLowerCase());
          if (termMatch && (!termValue || termValue.value !== termMatch.value)) setTermValue(termMatch);
        }
        
        if (user?.currentSession) {
          const sessionMatch = sessionOptions.find(opt => opt.label === user.currentSession);
          if (sessionMatch && (!sessionValue || sessionValue.value !== sessionMatch.value)) setSessionValue(sessionMatch);
        }
        
        initializedFromParams.current = true;
      }
    }
    // eslint-disable-next-line
  }, [classOptions, subjectOptions, classParam, subjectParam, currentSubject, user, termOptions, sessionOptions]);

  // Effect for filtering students
  useEffect(() => {
    if (searchTerm) {
      const filtered = studentsData.filter(student => 
        student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.admissionNo.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredStudents(filtered);
    } else {
      setFilteredStudents(studentsData);
    }
    setCurrentPage(1);
  }, [searchTerm, studentsData]);

  const totalPages = Math.ceil(filteredStudents.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentStudents = filteredStudents.slice(startIndex, endIndex);

  // Utility function to format decimal values
  const formatScore = (value) => {
    if (value === undefined || value === null || value === '') return '';
    
    const num = parseFloat(value);
    if (isNaN(num)) return '';
    
    // If the number ends with .0, show as integer
    if (num % 1 === 0) {
      return Math.floor(num).toString();
    }
    
    // Otherwise, show as is
    return num.toString();
  };

  const formatWorkSheetScore = (value) => {
    if (value === undefined || value === null || value === '') return '';
    
    const num = parseFloat(value);
    if (isNaN(num)) return '';
    
    // If the number ends with .0, show as integer
    if (num % 1 === 0) {
      return Math.floor(num);
    }
    
    // Otherwise, show as is
    return num;
  };

  // Calculate total and grade
  const calculateTotalAndGrade = (studentId, field, value) => {
    const updatedStudents = studentsData.map(student => {
      if (student.id === studentId) {
        const updatedScores = { ...student.scores, [field]: value };
        
        const ca1 = parseFloat(updatedScores.ca1) || 0;
        const ca2 = parseFloat(updatedScores.ca2) || 0;
        const assignment = parseFloat(updatedScores.assignment) || 0;
        const project = parseFloat(updatedScores.project) || 0;
        const exam = parseFloat(updatedScores.exam) || 0;
        
        const total = ca1 + ca2 + assignment + project + exam;
        
        let grade = '-';
        if (total >= 80) {
          grade = 'A (Excellent)';
        } else if (total >= 70) {
          grade = 'B (Very Good)';
        } else if (total >= 60) {
          grade = 'C (Good)';
        } else if (total >= 50) {
          grade = 'D (Fair)';
        } else if (total >= 40) {
          grade = 'E (Pass)';
        } else if (total > 0) {
          grade = 'F (Fail)';
        }
        
        return { 
          ...student, 
          scores: { 
            ...updatedScores, 
            total, 
            grade 
          },
          hasError: false,
          errorReason: null,
          fieldErrors: {}
        };
      }
      return student;
    });
    
    setStudentsData(updatedStudents);
  };

  // Handle score input changes
  const handleScoreChange = (studentId, field, value) => {
    if (value === '') {
      calculateTotalAndGrade(studentId, field, '');
      return;
    }

    let parsedValue = parseFloat(value);
    if (isNaN(parsedValue) || parsedValue < 0) {
      parsedValue = 0;
    }

    if (field === 'ca1' || field === 'ca2' || field === 'assignment' || field === 'project') {
      parsedValue = Math.min(10, parsedValue);
    }
    if (field === 'exam') {
      parsedValue = Math.min(60, parsedValue);
    }
    
    calculateTotalAndGrade(studentId, field, parsedValue.toString());
  };

  // Load students
  const handleLoadStudents = async () => {
    if (!classValue || !subjectValue || !termValue || !sessionValue) {
      alert('Please select Class, Subject, Term, and Session');
      return;
    }

    setIsLoadingResult(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/web/result/load-results`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user?.token || localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          teacher_id: user.id,
          classroom_slug: classValue.value,
          subject_slug: subjectValue.value,
          term: termValue.value,
          session: sessionValue.label
        })
      });

      const result = await response.json();
      
      if (result.success) {
        const mappedStudents = result?.data.map(student => ({
          id: student.student_id,
          name: student.student_name || `${student.firstname} ${student.lastname} ${student.middlename}`,
          admissionNo: student.admission_no,
          scores: {
            ca1: student.test1 == 0 ? '0' : student.test1 || '',
            ca2: student.test2 == 0 ? '0' : student.test2 || '',
            assignment: student.assignment == 0 ? '0' : student.assignment || '',
            project: student.project == 0 ? '0' : student.project || '',
            exam: student.exam || '',
            total: student.total_score || 0,
            grade: student.grade || '-'
          },
          hasError: false,
          errorReason: null,
          fieldErrors: {}
        }));

        setStudentsData(mappedStudents);
        setFilteredStudents(mappedStudents);
        setShowStudentsSection(true);
      } else {
        setStudentsData([]);
        alert('Failed to load students.');
      }

    } catch (error) {
      console.error('Error loading students:', error);
      alert('Error loading students. Please try again.');
    } finally {
      setIsLoadingResult(false);
    }
  };

  // Save a single student's score
  const saveStudentScore = async (student) => {
    const { ca1, ca2, assignment, project, exam } = student.scores;

    // if (!ca1 || !ca2 || !assignment || !project || !exam) {
    //   alert(`Please enter all scores for ${student.name}`);
    //   return;
    // }
    if (!ca1 && !ca2 && !assignment && !project && !exam) {
        alert(`Please enter at least one score for ${student.name}`);
        return;
      }

    const ca1Val = Number(ca1);
    const ca2Val = Number(ca2);
    const assignmentVal = Number(assignment);
    const projectVal = Number(project);
    const examVal = Number(exam);

    const fieldErrors = {
      ca1: isNaN(ca1Val) || ca1Val < 0 || ca1Val > 10,
      ca2: isNaN(ca2Val) || ca2Val < 0 || ca2Val > 10,
      assignment: isNaN(assignmentVal) || assignmentVal < 0 || assignmentVal > 10,
      project: isNaN(projectVal) || projectVal < 0 || projectVal > 10,
      exam: isNaN(examVal) || examVal < 0 || examVal > 60
    };

    if (Object.values(fieldErrors).some(v => v)) {
      const badFields = Object.keys(fieldErrors).filter(f => fieldErrors[f]).join(', ');
      alert(`Invalid values for ${student.name}: ${badFields}`);
      setStudentsData(prevData =>
        prevData.map(s =>
          s.id === student.id
            ? { ...s, hasError: true, errorReason: `Invalid: ${badFields}`, fieldErrors }
            : s
        )
      );
      return;
    }

    try {
      setSavingStudentId(student.id);
      const response = await fetch(`${API_BASE_URL}/api/web/result/save-single`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user?.token || localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          student_id: student.id,
          teacher_id: user.id,
          classroom_slug: classValue.value,
          subject_slug: subjectValue.value,
          session: sessionValue.label,
          term: termValue.value,
          test1: parseFloat(ca1),
          test2: parseFloat(ca2),
          assignment: parseFloat(assignment),
          project: parseFloat(project),
          exam: parseFloat(exam),
        })
      });

      const result = await response.json();

      if (result.success && result.data) {
        setStudentsData((prevData) =>
          prevData.map((s) =>
            s.id === student.id
              ? {
                  ...s,
                  scores: {
                    ...s.scores,
                    ca1: result.data.test1,
                    ca2: result.data.test2,
                    assignment: result.data.assignment,
                    project: result.data.project,
                    exam: result.data.exam,
                    total: result.data.total_score,
                    grade: result.data.grade
                  },
                  hasError: false,
                  errorReason: null,
                  fieldErrors: {}
                }
              : s
          )
        );

        alert(`Score for ${student.name} saved successfully!`);
      } else {
        alert(`Failed to save score for ${student.name}.`);
      }
    } catch (error) {
      console.error('Error saving score:', error);
      alert('An error occurred while saving the score.');
    } finally {
      setSavingStudentId(null);
    }
  };

  // Save all scores
  const saveAllScores1 = async () => {
    let hasError = false;
    const errorMessages = [];
    const updatedStudents = [];

    studentsData.forEach(student => {
      const { ca1, ca2, assignment, project, exam } = student.scores;

      const ca1Val = Number(ca1);
      const ca2Val = Number(ca2);
      const assignmentVal = Number(assignment);
      const projectVal = Number(project);
      const examVal = Number(exam);

      const fieldErrors = {
        ca1: isNaN(ca1Val) || ca1Val < 0 || ca1Val > 10,
        ca2: isNaN(ca2Val) || ca2Val < 0 || ca2Val > 10,
        assignment: isNaN(assignmentVal) || assignmentVal < 0 || assignmentVal > 10,
        project: isNaN(projectVal) || projectVal < 0 || projectVal > 10,
        exam: isNaN(examVal) || examVal < 0 || examVal > 60
      };

      const hasFieldError = Object.values(fieldErrors).some(v => v);

      if (!ca1 || !ca2 || !assignment || !project || !exam) {
        hasError = true;
        errorMessages.push(`${student.name}: Missing one or more scores.`);
      } else if (hasFieldError) {
        hasError = true;
        const badFields = Object.keys(fieldErrors).filter(f => fieldErrors[f]).join(', ');
        errorMessages.push(`${student.name}: Invalid value(s) in ${badFields}.`);
      }

      updatedStudents.push({
        ...student,
        hasError: hasFieldError,
        fieldErrors
      });
    });

    if (hasError) {
      setStudentsData(updatedStudents);
      alert(`Errors found:\n- ` + errorMessages.join('\n- '));
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/web/result/save-bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user?.token || localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          teacher_id: user.id,
          classroom_slug: classValue.value,
          subject_slug: subjectValue.value,
          term: termValue.value,
          session: sessionValue.label,
          exam_date: examDate,
          scores: studentsData.map(student => ({
            student_id: student.id,
            ca1: parseInt(student.scores.ca1),
            ca2: parseInt(student.scores.ca2),
            assignment: parseInt(student.scores.assignment),
            project: parseInt(student.scores.project),
            exam: parseInt(student.scores.exam)
          }))
        })
      });

      const result = await response.json();

      if (result.success && result.data) {
        setStudentsData(prevData =>
          prevData.map(student => {
            const updated = result.data.find(r => r.student_id === student.id);
            if (updated) {
              return {
                ...student,
                scores: {
                  ...student.scores,
                  ca1: updated.test1,
                  ca2: updated.test2,
                  assignment: updated.assignment,
                  project: updated.project,
                  exam: updated.exam,
                  total: updated.total_score,
                  grade: updated.grade || '-',
                  remark: updated.remark || '-'
                },
                hasError: false,
                fieldErrors: {}
              };
            }
            return student;
          })
        );
        alert('All scores saved successfully!');
      } else {
        alert('Failed to save all scores.');
      }

    } catch (error) {
      console.error('Error saving all scores:', error);
      alert('Error saving all scores. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  //submits students with complete and valid scores, and skips students with missing or invalid fields.
  const saveAllScores = async () => {
    const validStudents = [];
    const skippedStudents = [];
    const errorMessages = [];

    studentsData.forEach(student => {
      const { ca1, ca2, assignment, project, exam } = student.scores;

      // Check if all scores are empty
      if (
        (!ca1 && !ca2 && !assignment && !project && !exam)
        || [ca1, ca2, assignment, project, exam].every(v => v === '' || v === undefined || v === null)
      ) {
        skippedStudents.push(student.name);
        errorMessages.push(`${student.name}: No scores entered.`);
        return;
      }

      // Validate only fields that have values
      const fieldErrors = {
        ca1: ca1 !== '' && ca1 !== undefined && ca1 !== null ? (isNaN(Number(ca1)) || Number(ca1) < 0 || Number(ca1) > 10) : false,
        ca2: ca2 !== '' && ca2 !== undefined && ca2 !== null ? (isNaN(Number(ca2)) || Number(ca2) < 0 || Number(ca2) > 10) : false,
        assignment: assignment !== '' && assignment !== undefined && assignment !== null ? (isNaN(Number(assignment)) || Number(assignment) < 0 || Number(assignment) > 10) : false,
        project: project !== '' && project !== undefined && project !== null ? (isNaN(Number(project)) || Number(project) < 0 || Number(project) > 10) : false,
        exam: exam !== '' && exam !== undefined && exam !== null ? (isNaN(Number(exam)) || Number(exam) < 0 || Number(exam) > 60) : false,
      };

      const hasFieldError = Object.values(fieldErrors).some(v => v);

      if (hasFieldError) {
        skippedStudents.push(student.name);
        const badFields = Object.keys(fieldErrors).filter(f => fieldErrors[f]).join(', ');
        errorMessages.push(`${student.name}: Invalid value(s) in ${badFields}.`);
        return;
      }

      validStudents.push(student);
    });

    if (validStudents.length === 0) {
      alert(`No valid scores to save.\n${errorMessages.length ? 'Errors:\n- ' + errorMessages.join('\n- ') : ''}`);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/web/result/save-bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user?.token || localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          teacher_id: user.id,
          classroom_slug: classValue.value,
          subject_slug: subjectValue.value,
          term: termValue.value,
          session: sessionValue.label,
          exam_date: examDate,
          scores: validStudents.map(student => ({
            student_id: student.id,
            ca1: student.scores.ca1 ? parseFloat(student.scores.ca1) : null,
            ca2: student.scores.ca2 ? parseFloat(student.scores.ca2) : null,
            assignment: student.scores.assignment ? parseFloat(student.scores.assignment) : null,
            project: student.scores.project ? parseFloat(student.scores.project) : null,
            exam: student.scores.exam ? parseFloat(student.scores.exam) : null
          }))
        })
      });

      const result = await response.json();

      if (result.success && result.data) {
        setStudentsData(prevData =>
          prevData.map(student => {
            const updated = result.data.find(r => r.student_id === student.id);
            if (updated) {
              return {
                ...student,
                scores: {
                  ...student.scores,
                  ca1: updated.test1,
                  ca2: updated.test2,
                  assignment: updated.assignment,
                  project: updated.project,
                  exam: updated.exam,
                  total: updated.total_score,
                  grade: updated.grade || '-',
                  remark: updated.remark || '-'
                },
                hasError: false,
                fieldErrors: {}
              };
            }
            return student;
          })
        );
        let msg = `${validStudents.length} scores saved successfully!`;
        if (skippedStudents.length) {
          msg += `\n${skippedStudents.length} students skipped:\n- ${errorMessages.join('\n- ')}`;
        }
        alert(msg);
      } else {
        alert('Failed to save all scores.');
      }

    } catch (error) {
      console.error('Error saving all scores:', error);
      alert('Error saving all scores. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Reset all scores
  const resetScores = () => {
    if (window.confirm('Are you sure you want to reset all scores? This will clear all entered data.')) {
      const resetStudents = studentsData.map(student => ({
        ...student,
        scores: { ca1: '', ca2: '', assignment: '', project: '', exam: '', total: 0, grade: '-' },
        hasError: false,
        errorReason: null,
        fieldErrors: {}
      }));
      
      setStudentsData(resetStudents);
      setFilteredStudents(resetStudents);
    }
  };

  // File import functions
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      processImportFile(file);
    }
    event.target.value = null; 
  };

  const handleImportClick = () => {
    if (!classValue || !subjectValue) {
      alert('Please select Class and Subject before importing.');
      return;
    }
    if (!studentsData || studentsData.length === 0) {
      alert('Please load students for the selected class first.');
      return;
    }
    fileInputRef.current.click();
  };

  const processImportFile1 = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        if (!jsonData || jsonData.length === 0) {
          alert('The imported file is empty or could not be read.');
          return;
        }

        let updatedCount = 0;
        let errorCount = 0;
        const errorMessages = [];

        const updatedStudents = studentsData.map(student => {
          const importedRow = jsonData.find(row =>
            row['Admission No'] &&
            row['Admission No'].toString().trim() === student.admissionNo.toString().trim()
          );

          if (!importedRow) {
            errorCount++;
            errorMessages.push(`No match for Admission No: ${student.admissionNo}`);
            return { ...student, hasError: true, errorReason: 'Invalid admission number', fieldErrors: {} };
          }

          const ca1 = Number(importedRow['CA1']) || 0;
          const ca2 = Number(importedRow['CA2']) || 0;
          const assignment = Number(importedRow['Assignment']) || 0;
          const project = Number(importedRow['Project']) || 0;
          const exam = Number(importedRow['Exam']) || 0;

          const fieldErrors = {
            ca1: isNaN(ca1) || ca1 < 0 || ca1 > 10,
            ca2: isNaN(ca2) || ca2 < 0 || ca2 > 10,
            assignment: isNaN(assignment) || assignment < 0 || assignment > 10,
            project: isNaN(project) || project < 0 || project > 10,
            exam: isNaN(exam) || exam < 0 || exam > 60
          };

          const hasFieldError = Object.values(fieldErrors).some(val => val);

          if (hasFieldError) {
            errorCount++;
            const failedFields = Object.keys(fieldErrors)
              .filter(f => fieldErrors[f])
              .join(', ');
            errorMessages.push(`Invalid values for ${student.name} ${student.admissionNo}: [${failedFields}]`);
            return { ...student, hasError: true, errorReason: `Invalid: ${failedFields}`, fieldErrors };
          }

          const total = ca1 + ca2 + assignment + project + exam;
          let grade = '-';
          if (total >= 80) grade = 'A (Excellent)';
          else if (total >= 70) grade = 'B (Very Good)';
          else if (total >= 60) grade = 'C (Good)';
          else if (total >= 50) grade = 'D (Fair)';
          else if (total >= 40) grade = 'E (Pass)';
          else if (total > 0) grade = 'F (Fail)';

          updatedCount++;
          return {
            ...student,
            hasError: false,
            errorReason: null,
            fieldErrors: {},
            scores: {
              ca1: String(ca1),
              ca2: String(ca2),
              assignment: String(assignment),
              project: String(project),
              exam: String(exam),
              total,
              grade
            }
          };
        });

        setStudentsData(updatedStudents);

        let alertMsg = `${updatedCount} student records updated from import.`;
        if (errorCount > 0) {
          alertMsg += `\n${errorCount} errors found:\n- ` + errorMessages.join('\n- ');
        }

        alert(alertMsg);

      } catch (error) {
        console.error("Error processing import file:", error);
        alert('Failed to import scores. Please ensure the file is a valid Excel/CSV format with correct columns (e.g., "Admission No", "CA1", "CA2", "Assignment", "Project", "Exam").');
      }
    };

    reader.onerror = (error) => {
      console.error("File reading error:", error);
      alert('Failed to read the selected file.');
    };

    reader.readAsBinaryString(file);
  };

  const processImportFile2 = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const sheetData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (!sheetData || sheetData.length === 0) {
          alert('The imported file is empty or could not be read.');
          return;
        }

        let startRow = 0;
        let impSubject = '';
        let impClass = '';
        if (
          sheetData.length > 0 &&
          sheetData[0].length >= 2 &&
          typeof sheetData[0][0] === 'string' &&
          sheetData[0][0].startsWith('Subject:') &&
          typeof sheetData[0][1] === 'string' &&
          sheetData[0][1].startsWith('Class:')
        ) {
          impSubject = sheetData[0][0].replace('Subject:', '').trim();
          impClass = sheetData[0][1].replace('Class:', '').trim();
          startRow = 1;
        }

        // Compare imported subject and class if present
        if (startRow === 1) {
          if (
            impSubject.toLowerCase() !== subjectValue.label.toLowerCase() ||
            impClass.toLowerCase() !== classValue.label.toLowerCase()
          ) {
            if (
              !window.confirm(
                `Imported file is for ${impSubject} - ${impClass}, but selected is ${subjectValue.label} - ${classValue.label}. Proceed anyway?`
              )
            ) {
              return;
            }
          }
        }

        const headers = sheetData[startRow];
        if (!headers) {
          alert('No headers found in the file.');
          return;
        }

        const admNoIdx = headers.findIndex(h => h === 'Admission No' || h === 'Admission No');
        const nameIdx = headers.findIndex(h => h === 'Name');
        const ca1Idx = headers.findIndex(h => h === 'CA1');
        const ca2Idx = headers.findIndex(h => h === 'CA2');
        const assignmentIdx = headers.findIndex(h => h === 'Assignment');
        const projectIdx = headers.findIndex(h => h === 'Project');
        const examIdx = headers.findIndex(h => h === 'Exam');

        if (admNoIdx === -1) {
          alert('Required column "Admission No" not found in the file.');
          return;
        }

        const dataStart = startRow + 1;
        const jsonData = sheetData.slice(dataStart).map(row => ({
          'Admission No': row[admNoIdx],
          Name: nameIdx !== -1 ? row[nameIdx] : undefined,
          CA1: ca1Idx !== -1 ? row[ca1Idx] : undefined,
          CA2: ca2Idx !== -1 ? row[ca2Idx] : undefined,
          Assignment: assignmentIdx !== -1 ? row[assignmentIdx] : undefined,
          Project: projectIdx !== -1 ? row[projectIdx] : undefined,
          Exam: examIdx !== -1 ? row[examIdx] : undefined,
        }));

        let updatedCount = 0;
        let errorCount = 0;
        const errorMessages = [];

        const updatedStudents = studentsData.map(student => {
          const importedRow = jsonData.find(row =>
            row['Admission No'] &&
            row['Admission No'].toString().trim() === student.admissionNo.toString().trim()
          );

          if (!importedRow) {
            errorCount++;
            errorMessages.push(`No match for Admission No: ${student.admissionNo}`);
            return { ...student, hasError: true, errorReason: 'Invalid admission number', fieldErrors: {} };
          }

          const ca1 = Number(importedRow['CA1']) || 0;
          const ca2 = Number(importedRow['CA2']) || 0;
          const assignment = Number(importedRow['Assignment']) || 0;
          const project = Number(importedRow['Project']) || 0;
          const exam = Number(importedRow['Exam']) || 0;

          const fieldErrors = {
            ca1: isNaN(ca1) || ca1 < 0 || ca1 > 10,
            ca2: isNaN(ca2) || ca2 < 0 || ca2 > 10,
            assignment: isNaN(assignment) || assignment < 0 || assignment > 10,
            project: isNaN(project) || project < 0 || project > 10,
            exam: isNaN(exam) || exam < 0 || exam > 60
          };

          const hasFieldError = Object.values(fieldErrors).some(val => val);

          if (hasFieldError) {
            errorCount++;
            const failedFields = Object.keys(fieldErrors)
              .filter(f => fieldErrors[f])
              .join(', ');
            errorMessages.push(`Invalid values for ${student.name} ${student.admissionNo}: [${failedFields}]`);
            return { ...student, hasError: true, errorReason: `Invalid: ${failedFields}`, fieldErrors };
          }

          const total = ca1 + ca2 + assignment + project + exam;
          let grade = '-';
          if (total >= 80) grade = 'A (Excellent)';
          else if (total >= 70) grade = 'B (Very Good)';
          else if (total >= 60) grade = 'C (Good)';
          else if (total >= 50) grade = 'D (Fair)';
          else if (total >= 40) grade = 'E (Pass)';
          else if (total > 0) grade = 'F (Fail)';

          updatedCount++;
          return {
            ...student,
            hasError: false,
            errorReason: null,
            fieldErrors: {},
            scores: {
              ca1: String(ca1),
              ca2: String(ca2),
              assignment: String(assignment),
              project: String(project),
              exam: String(exam),
              total,
              grade
            }
          };
        });

        setStudentsData(updatedStudents);

        let alertMsg = `${updatedCount} student records updated from import.`;
        if (errorCount > 0) {
          alertMsg += `\n${errorCount} errors found:\n- ` + errorMessages.join('\n- ');
        }

        alert(alertMsg);

      } catch (error) {
        console.error("Error processing import file:", error);
        alert('Failed to import scores. Please ensure the file is a valid Excel/CSV format with correct columns (e.g., "Admission No", "Name", "CA1", "CA2", "Assignment", "Project", "Exam").');
      }
    };

    reader.onerror = (error) => {
      console.error("File reading error:", error);
      alert('Failed to read the selected file.');
    };

    reader.readAsBinaryString(file);
  };

  const processImportFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        // const jsonData = XLSX.utils.sheet_to_json(worksheet);
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { range: 2 }); //Since real table begins on Row 3 (index 2)

        // console.log({sheetName});

        if (!jsonData || jsonData.length === 0) {
          alert('The imported file is empty or could not be read.');
          return;
        }

        let updatedCount = 0;
        let errorCount = 0;
        const errorMessages = [];

        const updatedStudents = studentsData.map(student => {
          const importedRow = jsonData.find(row =>
            row['Admission No'] &&
            row['Admission No'].toString().trim() === student.admissionNo.toString().trim()
          );

          if (!importedRow) {
            errorCount++;
            errorMessages.push(`No match for Admission No: ${student.admissionNo}`);
            // return { ...student, hasError: true, errorReason: 'Invalid admission number', fieldErrors: {} };
            return { ...student, hasError: true, errorReason: 'No match for admission number', fieldErrors: {} };
          }

          const ca1 = Number(importedRow['CA1']) || 0;
          const ca2 = Number(importedRow['CA2']) || 0;
          const assignment = Number(importedRow['Assignment']) || 0;
          const project = Number(importedRow['Project']) || 0;
          const exam = Number(importedRow['Exam']) || 0;

          const fieldErrors = {
            ca1: isNaN(ca1) || ca1 < 0 || ca1 > 10,
            ca2: isNaN(ca2) || ca2 < 0 || ca2 > 10,
            assignment: isNaN(assignment) || assignment < 0 || assignment > 10,
            project: isNaN(project) || project < 0 || project > 10,
            exam: isNaN(exam) || exam < 0 || exam > 60
          };

          const hasFieldError = Object.values(fieldErrors).some(val => val);

          if (hasFieldError) {
            errorCount++;
            const failedFields = Object.keys(fieldErrors)
              .filter(f => fieldErrors[f])
              .join(', ');
            errorMessages.push(`Invalid values for ${student.name} ${student.admissionNo}: [${failedFields}]`);
            return { ...student, hasError: true, errorReason: `Invalid: ${failedFields}`, fieldErrors };
          }

          const total = ca1 + ca2 + assignment + project + exam;
          let grade = '-';
          if (total >= 80) grade = 'A (Excellent)';
          else if (total >= 70) grade = 'B (Very Good)';
          else if (total >= 60) grade = 'C (Good)';
          else if (total >= 50) grade = 'D (Fair)';
          else if (total >= 40) grade = 'E (Pass)';
          else if (total > 0) grade = 'F (Fail)';

          updatedCount++;
          return {
            ...student,
            hasError: false,
            errorReason: null,
            fieldErrors: {},
            scores: {
              ca1: String(ca1),
              ca2: String(ca2),
              assignment: String(assignment),
              project: String(project),
              exam: String(exam),
              total,
              grade
            }
          };
        });

        setStudentsData(updatedStudents);

        let alertMsg = `${updatedCount} student records updated from import.`;
        if (errorCount > 0) {
          alertMsg += `\n${errorCount} errors found:\n- ` + errorMessages.join('\n- ');
        }

        alert(alertMsg);

      } catch (error) {
        console.error("Error processing import file:", error);
        alert('Failed to import scores. Please ensure the file is a valid Excel/CSV format with correct columns (e.g., "Admission No", "CA1", "CA2", "Assignment", "Project", "Exam").');
      }
    };

    reader.onerror = (error) => {
      console.error("File reading error:", error);
      alert('Failed to read the selected file.');
    };

    reader.readAsBinaryString(file);
  };

  const downloadSample1 = () => {
    const sampleData = [
      {
        'Admission No': '',
        'CA1': '',
        'CA2': '',
        'Assignment': '',
        'Project': '',
        'Exam': ''
      }
    ];
    const ws = XLSX.utils.json_to_sheet(sampleData, { 
      header: ['Admission No','CA1','CA2','Assignment','Project','Exam']
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'SampleScores');
    XLSX.writeFile(wb, 'sample-scores-template.xlsx');
  };

  const downloadSample2 = () => {
    const headerRow = [
      `Subject: ${subjectValue?.label || ''}`,
      `Class: ${classValue?.label || ''}`
    ];
    const columnHeaders = ['Admission No', 'Name', 'CA1', 'CA2', 'Assignment', 'Project', 'Exam'];

    const allRows = [headerRow, columnHeaders];
    const ws = XLSX.utils.aoa_to_sheet(allRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'SampleScores');
    XLSX.writeFile(wb, 'sample-scores-template.xlsx');
  };

  const downloadSample3 = () => {
    if (!studentsData.length) {
      alert("No students loaded. Please load students first.");
      return;
    }

    const subjectLabel = subjectValue?.label || "";
    const classLabel = classValue?.label || "";
    const termLabel = termValue?.label || "";
    const sessionLabel = sessionValue?.label || "";

    // Sheet structure
    const sampleSheetData = [
      [subjectLabel, classLabel, termLabel, sessionLabel, ""],
      [], // blank row
      ["Admission No", "Student Name", "CA1", "CA2", "Exam"],
    ];

    studentsData.forEach(student =>
      sampleSheetData.push([
        student.admissionNo,
        student.name,
        student.scores?.ca1 || "",
        student.scores?.ca2 || "",
        student.scores?.exam || ""
      ])
    );

    const worksheet = XLSX.utils.aoa_to_sheet(sampleSheetData);

    // Apply styles
    const headerStyle = {
      font: { bold: true, sz: 12 },
      alignment: { horizontal: "center" }
    };

    ["A3", "B3", "C3", "D3", "E3"].forEach(cell => {
      if (worksheet[cell]) worksheet[cell].s = headerStyle;
    });   

    // Column widths
    worksheet["!cols"] = [
      { wch: 15 }, // Admission No
      { wch: 30 }, // Name
      { wch: 10 }, // CA1
      { wch: 10 }, // CA2
      { wch: 10 }, // Exam
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sample Upload");

    XLSX.writeFile(
      workbook,
      `${classLabel}-${subjectLabel}-result-template.xlsx`
    );
  };

  const downloadSample = () => {
    if (!studentsData.length) {
      alert("No students loaded. Please load students first.");
      return;
    }

    const subjectLabel = subjectValue?.label || "";
    const classLabel = classValue?.label || "";
    const termLabel = termValue?.label || "";
    const sessionLabel = sessionValue?.label || "";

    // Sheet structure
    const sampleSheetData = [
      [subjectLabel, classLabel, termLabel, sessionLabel, ""],
      [], // blank row
      ["Admission No", "Student Name", "CA1", "CA2", "Assignment", "Project", "Exam"]
    ];

    // studentsData.forEach(student =>
    //   sampleSheetData.push([
    //     student.admissionNo,
    //     student.name,
    //     student.scores?.ca1 || "",
    //     student.scores?.ca2 || "",
    //     student.scores?.exam || ""
    //   ])
    // );

    studentsData.forEach(student => {
      sampleSheetData.push([
        student.admissionNo,
        student.name,
        student.scores?.ca1 === "0" ? 0 : formatWorkSheetScore(student.scores?.ca1) || "",
        student.scores?.ca2 === "0" ? 0 : formatWorkSheetScore(student.scores?.ca2) || "",
        student.scores?.assignment === "0" ? 0 : formatWorkSheetScore(student.scores?.assignment) || "",
        student.scores?.project === "0" ? 0 : formatWorkSheetScore(student.scores?.project) || "",
        student.scores?.exam === "0" ? 0 : formatWorkSheetScore(student.scores?.exam) || ""
      ]);
    });

    const worksheet = XLSX.utils.aoa_to_sheet(sampleSheetData);

    // Apply styles
    const headerStyle = {
      font: { bold: true, sz: 12 },
      alignment: { horizontal: "center" },
      border: {
        top: { style: "thin" },
        bottom: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" }
      }
    };

    ["A3", "B3", "C3", "D3", "E3", "F3", "G3"].forEach(cell => {
      if (worksheet[cell]) worksheet[cell].s = headerStyle;
    });   

    // Column widths
    worksheet["!cols"] = [
      { wch: 15 }, // Admission No
      { wch: 35 }, // Name
      { wch: 10 }, // CA1
      { wch: 10 }, // CA2
      { wch: 10 }, // ASSIGNMENT
      { wch: 10 }, // PROJECT
      { wch: 10 }, // Exam
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, `${classLabel}-result`);

    XLSX.writeFile(
      workbook,
      `${classLabel}-${subjectLabel}-result-template.xlsx`
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Enter Class Results
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Manage student scores for your assigned classes
              </p>
            </div>
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto p-6">
        {/* Selection form */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm py-6 px-2 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Class
              </label>
              <Select
                options={classOptions}
                value={classValue}
                onChange={(value) => {
                  classManuallyChanged.current = true;
                  setClassValue(value);
                  // Clear query param from URL
                  const newSearchParams = new URLSearchParams(location.search);
                  newSearchParams.delete('classOption');
                  navigate(`${location.pathname}${newSearchParams.toString() ? '?' + newSearchParams.toString() : ''}`, { replace: true });
                }}
                placeholder="Select Class"
                className="basic-select"
                classNamePrefix="select"
                isDisabled={!!classId}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Subject
              </label>
              <Select
                options={subjectOptions}
                value={subjectValue}
                onChange={(value) => {
                  subjectManuallyChanged.current = true;
                  setSubjectValue(value);
                  // Clear query param from URL
                  const newSearchParams = new URLSearchParams(location.search);
                  newSearchParams.delete('subjectOption');
                  navigate(`${location.pathname}${newSearchParams.toString() ? '?' + newSearchParams.toString() : ''}`, { replace: true });
                }}
                placeholder="Select Subject"
                className="basic-select"
                classNamePrefix="select"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Term
              </label>
              <Select
                options={termOptions}
                value={termValue}
                onChange={setTermValue}
                placeholder="Select Term"
                className="basic-select"
                classNamePrefix="select"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Session
              </label>
              <Select
                options={sessionOptions}
                value={sessionValue}
                onChange={setSessionValue}
                placeholder="Select Session"
                className="basic-select"
                classNamePrefix="select"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Exam Date (optional)
              </label>
              <input
                type="date"
                value={examDate}
                onChange={(e) => setExamDate(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
            
            {/* <div className="flex items-end gap-3 md:col-span-3"> */}
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={handleLoadStudents}
                // className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                className="flex items-center justify-center gap-2 px-3 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium order-first"
                disabled={isLoadingResult}
              >
                {isLoadingResult ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4" />
                    Load Students
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={handleImportClick}
                // className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                className="flex items-center justify-center gap-2 px-3 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
              >
                <Upload className="w-4 h-4" />
                Import Scores
              </button>
              <button
                type="button"
                onClick={downloadSample}
                // className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                className="flex items-center justify-center gap-2 px-3 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium sm:order-last"
              >
                <Download className="w-4 h-4" />
                Download Sample
              </button>
              <input 
                type="file" 
                ref={fileInputRef} 
                onChange={handleFileChange}
                className="hidden" 
                accept=".xlsx, .xls, .csv"
              />
            </div>
            
          </div>
        </div>

        {/* Students scores table */}
        {showStudentsSection && (
          // <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm -mx-4 sm:mx-0 sm:rounded-xl overflow-hidden">
            
            <div className="px-4 sm:px-6 pt-6 pb-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-medium mb-4 text-gray-900 dark:text-white">
                Enter Scores: {classValue?.label} - {subjectValue?.label}
              </h3>

              {/* <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search student..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <Search className="w-5 h-5 text-gray-400 dark:text-gray-300" />
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className="text-gray-700 dark:text-gray-300">Show:</span>
                    <select
                      value={rowsPerPage}
                      onChange={(e) => setRowsPerPage(Number(e.target.value))}
                      className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </select>
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={resetScores}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Reset
                  </button>
                  <button
                    type="button"
                    onClick={saveAllScores}
                    className={`flex items-center gap-2 px-4 py-2 ${loading ? 'bg-yellow-500' : 'bg-blue-600'} text-white rounded-lg hover:bg-blue-700 transition-colors`}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        Save All Scores
                      </>
                    )}
                  </button>
                </div>
              </div> */}

              {/* Search + Show + Actions */}
              <div className="mt-4 px-4 sm:px-0">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <div className="relative w-full sm:w-auto">
                      <input
                        type="text"
                        placeholder="Search student..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 pr-4 py-2.5 w-full sm:w-64 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      />
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Show:</span>
                      <select
                        value={rowsPerPage}
                        onChange={(e) => setRowsPerPage(Number(e.target.value))}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={resetScores}
                      className="px-4 py-2.5 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition flex items-center gap-2 text-sm font-medium"
                    >
                      <RotateCcw className="w-4 h-4" /> Reset
                    </button>
                    <button
                      onClick={saveAllScores}
                      disabled={loading}
                      className={`px-6 py-2.5 rounded-lg font-medium flex items-center gap-2 transition ${
                        loading 
                          ? 'bg-yellow-500 text-white' 
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {loading ? (
                        <>Saving...</>
                      ) : (
                        <>Save All Scores</>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              {/* <table className="min-w-full border-collapse border border-gray-300 dark:border-gray-600"> */}
                <table className="w-full min-w-[900px] border-collapse">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-700">
                    <th className="border-r border-b border-gray-300 dark:border-gray-600 px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Student Name</th>
                    <th className="border-r border-b border-gray-300 dark:border-gray-600 px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Test 1<br />(10%)</th>
                    <th className="border-r border-b border-gray-300 dark:border-gray-600 px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Test 2<br />(10%)</th>
                    <th className="border-r border-b border-gray-300 dark:border-gray-600 px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Assignment<br />(10%)</th>
                    <th className="border-r border-b border-gray-300 dark:border-gray-600 px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Project<br />(10%)</th>
                    <th className="border-r border-b border-gray-300 dark:border-gray-600 px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Exam<br />(60%)</th>
                    <th className="border-r border-b border-gray-300 dark:border-gray-600 px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Total<br />(100%)</th>
                    <th className="border-r border-b border-gray-300 dark:border-gray-600 px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Grade</th>
                    <th className="border-b border-gray-300 dark:border-gray-600 px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                  {currentStudents.map((student, index, array) => (
                    <tr 
                      key={student.id} 
                      className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${student.hasError ? 'bg-red-100 dark:bg-red-900/20' : ''} 
                      ${index === array.length - 1 ? 'border-b-0' : ''}`}
                    >
                      <td className={`border-r ${index === array.length - 1 ? 'border-b-0' : 'border-b'} border-gray-300 dark:border-gray-600 px-6 py-4 whitespace-nowrap`}>
                        <div className="text-gray-900 dark:text-white">{student.name}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{student.admissionNo}</div>
                        {student.hasError && (
                          <div className="text-sm text-red-600 dark:text-red-400 font-semibold flex items-center gap-1">
                            <AlertCircle className="w-4 h-4" />
                            {student.errorReason}
                          </div>
                        )}
                      </td>
                      <td className={`border-r ${index === array.length - 1 ? 'border-b-0' : 'border-b'} border-gray-300 dark:border-gray-600 px-6 py-4 whitespace-nowrap text-center`}>
                        <input
                          type="number"
                          min="0"
                          max="10"
                          readOnly={user?.can_enter_score ? false : true}
                          // value={student.scores.ca1 || ''}
                          // value={student.scores.ca1 === undefined || student.scores.ca1 === null ? '' : student.scores.ca1}
                          value={formatScore(student.scores.ca1)}
                          onChange={(e) => handleScoreChange(student.id, 'ca1', e.target.value)}
                          className={`w-20 px-2 py-1 border ${student.fieldErrors?.ca1 ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-md text-center focus:outline-none focus:ring-2 ${student.fieldErrors?.ca1 ? 'focus:ring-red-500' : 'focus:ring-blue-500'} dark:bg-gray-700 dark:text-white`}
                        />
                      </td>
                      <td className={`border-r ${index === array.length - 1 ? 'border-b-0' : 'border-b'} border-gray-300 dark:border-gray-600 px-6 py-4 whitespace-nowrap text-center`}>
                        <input
                          type="number"
                          min="0"
                          max="10"
                          readOnly={user?.can_enter_score ? false : true}
                          // value={student.scores.ca2 || ''}
                          // value={student.scores.ca2 === undefined || student.scores.ca2 === null ? '' : student.scores.ca2}
                          value={formatScore(student.scores.ca2)}
                          onChange={(e) => handleScoreChange(student.id, 'ca2', e.target.value)}
                          className={`w-20 px-2 py-1 border ${student.fieldErrors?.ca2 ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-md text-center focus:outline-none focus:ring-2 ${student.fieldErrors?.ca2 ? 'focus:ring-red-500' : 'focus:ring-blue-500'} dark:bg-gray-700 dark:text-white`}
                        />
                      </td>
                      <td className={`border-r ${index === array.length - 1 ? 'border-b-0' : 'border-b'} border-gray-300 dark:border-gray-600 px-6 py-4 whitespace-nowrap text-center`}>
                        <input
                          type="number"
                          min="0"
                          max="10"
                          readOnly={user?.can_enter_score ? false : true}
                          // value={student.scores.assignment || ''}
                          // value={student.scores.assignment === undefined || student.scores.assignment === null ? '' : student.scores.assignment}
                          value={formatScore(student.scores.assignment)}
                          onChange={(e) => handleScoreChange(student.id, 'assignment', e.target.value)}
                          className={`w-20 px-2 py-1 border ${student.fieldErrors?.assignment ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-md text-center focus:outline-none focus:ring-2 ${student.fieldErrors?.assignment ? 'focus:ring-red-500' : 'focus:ring-blue-500'} dark:bg-gray-700 dark:text-white`}
                        />
                      </td>
                      <td className={`border-r ${index === array.length - 1 ? 'border-b-0' : 'border-b'} border-gray-300 dark:border-gray-600 px-6 py-4 whitespace-nowrap text-center`}>
                        <input
                          type="number"
                          min="0"
                          max="10"
                          readOnly={user?.can_enter_score ? false : true}
                          // value={student.scores.project || ''}
                          // value={student.scores.project === undefined || student.scores.project === null ? '' : student.scores.project}
                          value={formatScore(student.scores.project)}
                          onChange={(e) => handleScoreChange(student.id, 'project', e.target.value)}
                          className={`w-20 px-2 py-1 border ${student.fieldErrors?.project ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-md text-center focus:outline-none focus:ring-2 ${student.fieldErrors?.project ? 'focus:ring-red-500' : 'focus:ring-blue-500'} dark:bg-gray-700 dark:text-white`}
                        />
                      </td>
                      <td className={`border-r ${index === array.length - 1 ? 'border-b-0' : 'border-b'} border-gray-300 dark:border-gray-600 px-6 py-4 whitespace-nowrap text-center`}>
                        <input
                          type="number"
                          min="0"
                          max="60"
                          readOnly={user?.can_enter_score ? false : true}
                          // value={student.scores.exam || ''}
                          // value={student.scores.exam === undefined || student.scores.exam === null ? '' : student.scores.exam}
                          value={formatScore(student.scores.exam)}
                          onChange={(e) => handleScoreChange(student.id, 'exam', e.target.value)}
                          className={`w-20 px-2 py-1 border ${student.fieldErrors?.exam ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-md text-center focus:outline-none focus:ring-2 ${student.fieldErrors?.exam ? 'focus:ring-red-500' : 'focus:ring-blue-500'} dark:bg-gray-700 dark:text-white`}
                        />
                      </td>
                      <td className={`border-r ${index === array.length - 1 ? 'border-b-0' : 'border-b'} border-gray-300 dark:border-gray-600 px-6 py-4 whitespace-nowrap text-center font-medium bg-gray-50 dark:bg-gray-700`}>
                        {/* {student.scores.total} */}
                        {formatScore(student.scores.total)}
                      </td>
                      <td className={`border-r ${index === array.length - 1 ? 'border-b-0' : 'border-b'} border-gray-300 dark:border-gray-600 px-6 py-4 whitespace-nowrap text-center`}>
                        {student.scores.grade}
                      </td>
                      <td className={`${index === array.length - 1 ? 'border-b-0' : 'border-b'} border-gray-300 dark:border-gray-600 px-6 py-4 whitespace-nowrap text-center`}>
                        <button
                          type="button"
                          onClick={() => saveStudentScore(student)}
                          className="w-8 h-8 flex items-center justify-center bg-green-600 text-white rounded-full hover:bg-green-700 transition-colors"
                          title="Save Score"
                          disabled={savingStudentId === student.id}
                        >
                          {savingStudentId === student.id ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <Save className="w-4 h-4" />
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            <div className="flex flex-wrap justify-between items-center mt-6">
              <div className="flex justify-center my-4">
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="w-10 h-10 flex items-center justify-center bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-10 h-10 flex items-center justify-center rounded-md ${
                        currentPage === page
                          ? 'bg-blue-600 text-white'
                          : 'bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="w-10 h-10 flex items-center justify-center bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={resetScores}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                  Reset
                </button>
                <button
                  type="button"
                  onClick={saveAllScores}
                  className={`flex items-center gap-2 px-4 py-2 ${loading ? 'bg-yellow-500' : 'bg-blue-600'} text-white rounded-lg hover:bg-blue-700 transition-colors`}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Save All Scores
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnterClassResult;
